// =============================================================================
// MeuDriver — Student Dashboard
// =============================================================================

import { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useSEO } from '@/hooks/useSEO'
import {
  Calendar,
  CheckCircle,
  Heart,
  Search,
  MapPin,
  Clock,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Car,
  Sparkles,
  Trophy,
  Target,
  Zap,
  Star,
  TrendingUp,
  Gift,
  Copy,
  Share2,
  History,
  Bell,
} from 'lucide-react'
import { motion } from 'framer-motion'

import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useStudentBookings, useStudentReviewedBookingIds } from '@/hooks/useBookings'
import { useFavorites, useToggleFavorite } from '@/hooks/useFavorites'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  formatDateTime,
  getBookingStatusLabel,
  getBookingStatusColor,
  getInitials,
  formatCurrency,
  cn,
} from '@/lib/utils'
import type { BookingWithDetails, InstructorWithProfile } from '@/types/database'
import { RatingStars } from '@/components/instructor/RatingStars'
import { OnboardingTour } from '@/components/shared/OnboardingTour'
import { WelcomeBanner } from '@/components/shared/WelcomeBanner'
import { LoyaltyPointsCard } from '@/components/student/LoyaltyPointsCard'
import { ActivePackageWidget } from '@/components/student/ActivePackageWidget'
import {
  format, parseISO, isFuture, isPast,
  startOfMonth, endOfMonth, eachDayOfInterval,
  isSameDay, isSameMonth, addMonths, subMonths, isToday,
  differenceInHours,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useNextLesson, useStudentMonthSummary } from '@/hooks/useStudentDashboardData'
import { LearningPathTimeline } from '@/components/student/LearningPathTimeline'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatLessonDateTime(dateStr: string): string {
  try {
    const date = parseISO(dateStr)
    const dayName = format(date, 'EEEE', { locale: ptBR })
    const dayCapitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1)
    const dayNum = format(date, 'd', { locale: ptBR })
    const monthName = format(date, 'MMMM', { locale: ptBR })
    const time = format(date, "HH'h'mm", { locale: ptBR })
    return `${dayCapitalized}, ${dayNum} de ${monthName} • ${time}`
  } catch {
    return formatDateTime(dateStr)
  }
}

// ─── Animation variants ───────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
}

// ─── Skeleton loaders ─────────────────────────────────────────────────────────

function StatCardSkeleton() {
  return (
    <div className="rounded-2xl bg-white shadow-soft p-5 border border-border/60">
      <Skeleton className="h-10 w-10 rounded-xl mb-3" />
      <Skeleton className="h-8 w-14 mb-1.5" />
      <Skeleton className="h-3.5 w-24" />
    </div>
  )
}

function BookingCardSkeleton() {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border bg-white">
      <Skeleton className="h-10 w-10 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
      <Skeleton className="h-6 w-20 rounded-full" />
    </div>
  )
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode
  value: number
  label: string
  gradient: string
  delay?: number
}

function StatCard({ icon, value, label, gradient, delay = 0 }: StatCardProps) {
  return (
    <motion.div
      variants={fadeUp}
      custom={delay}
      className="rounded-2xl bg-white shadow-soft border border-border/60 p-5 flex flex-col gap-2 hover:shadow-card transition-shadow duration-300"
    >
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${gradient}`}>
        {icon}
      </div>
      <p className="text-3xl font-extrabold text-gray-900 tracking-tight animate-number-in">
        {value}
      </p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </motion.div>
  )
}

// ─── Upcoming Booking Card ────────────────────────────────────────────────────

interface UpcomingCardProps {
  booking: BookingWithDetails
}

function UpcomingCard({ booking }: UpcomingCardProps) {
  const instructor = booking.instructor
  const instructorName = instructor?.profile?.full_name ?? 'Instrutor'
  const avatarUrl = instructor?.profile?.avatar_url ?? undefined
  const statusColor = getBookingStatusColor(booking.status)
  const statusLabel = getBookingStatusLabel(booking.status)

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="flex items-center gap-4 p-4 rounded-xl border border-border/60 bg-white hover:shadow-soft transition-shadow duration-200"
    >
      <Avatar className="h-11 w-11 shrink-0 ring-2 ring-border">
        <AvatarImage src={avatarUrl} />
        <AvatarFallback className="bg-brand-100 text-brand-700 font-semibold text-sm">
          {getInitials(instructorName)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm truncate">{instructorName}</p>
        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
          <Clock className="h-3 w-3 shrink-0 text-brand-400" />
          {formatLessonDateTime(booking.scheduled_at)}
        </p>
        {booking.pickup_address && (
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 shrink-0 text-teal-500" />
            {booking.pickup_address}
          </p>
        )}
      </div>

      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
          {statusLabel}
        </span>
        <Link
          to={`/aluno/aulas/${booking.id}`}
          className="text-xs text-brand-600 font-medium hover:text-brand-700 flex items-center gap-0.5 transition-colors"
        >
          Detalhes
          <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </motion.div>
  )
}

// ─── Recent (Completed) Booking Card ─────────────────────────────────────────

// ─── Favorite Instructor Card ─────────────────────────────────────────────────

interface FavCardProps {
  instructor: InstructorWithProfile
  onUnfav: (id: string) => void
}

function FavCard({ instructor, onUnfav }: FavCardProps) {
  const name = instructor.profile?.full_name ?? 'Instrutor'
  const city = instructor.profile?.city
  const slug = instructor.slug ?? instructor.id

  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border/60 bg-white hover:shadow-soft transition-shadow duration-200">
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarImage src={instructor.profile?.avatar_url ?? undefined} />
        <AvatarFallback className="bg-brand-100 text-brand-700 text-sm font-semibold">
          {getInitials(name)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-gray-900 truncate">{name}</p>
        {city && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3 text-teal-500" />
            {city}
          </p>
        )}
        <RatingStars rating={instructor.rating_avg} size="sm" showValue />
      </div>

      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <Link to={`/instrutores/${slug}`}>
          <Button size="sm" variant="outline" className="h-7 text-xs hover:border-brand-300 hover:text-brand-700">
            Ver perfil
          </Button>
        </Link>
        <button
          onClick={() => onUnfav(instructor.id)}
          className="text-xs text-rose-400 hover:text-rose-600 flex items-center gap-0.5 transition-colors"
        >
          <Heart className="h-3 w-3 fill-current" />
          Remover
        </button>
      </div>
    </div>
  )
}

// ─── Empty State ─────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon: React.ReactNode
  title: string
  description: string
  action?: React.ReactNode
}

function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center py-12 gap-4 text-center rounded-2xl border border-dashed border-border bg-white/60">
      <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="font-semibold text-gray-700">{title}</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">{description}</p>
      </div>
      {action}
    </div>
  )
}

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({
  icon,
  title,
  action,
}: {
  icon: React.ReactNode
  title: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {action}
    </div>
  )
}

// ─── Progress / Achievements ─────────────────────────────────────────────────

interface Achievement {
  id: string
  label: string
  icon: React.ReactNode
  color: string
  requiredLessons: number
}

const ACHIEVEMENTS: Achievement[] = [
  { id: 'first',    label: '1ª aula',      icon: <Zap className="h-4 w-4" />,        color: 'bg-yellow-100 text-yellow-700',  requiredLessons: 1  },
  { id: 'five',     label: '5 aulas',      icon: <Star className="h-4 w-4" />,        color: 'bg-brand-100 text-brand-700',    requiredLessons: 5  },
  { id: 'ten',      label: '10 aulas',     icon: <Trophy className="h-4 w-4" />,      color: 'bg-purple-100 text-purple-700',  requiredLessons: 10 },
  { id: 'twenty',   label: '20 aulas',     icon: <TrendingUp className="h-4 w-4" />,  color: 'bg-emerald-100 text-emerald-700',requiredLessons: 20 },
  { id: 'fifty',    label: '50 aulas',     icon: <Target className="h-4 w-4" />,      color: 'bg-orange-100 text-orange-700',  requiredLessons: 50 },
]

const CNH_GOAL_HOURS = 20 // meta padrão de horas para CNH

// ─── K4 Dashboard Widgets ─────────────────────────────────────────────────────

/** Widget: Próxima Aula */
function NextLessonWidget() {
  const { data: lesson, isLoading } = useNextLesson()

  if (isLoading) {
    return (
      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-5 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    )
  }

  if (!lesson) {
    return (
      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-5 flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Próxima aula</p>
          <p className="text-sm text-gray-500 mt-1">Nenhuma aula agendada</p>
          <Link to="/aluno/instrutores" className="text-xs text-brand-600 font-medium hover:text-brand-700 mt-1">
            Agendar agora
          </Link>
        </CardContent>
      </Card>
    )
  }

  const instructorName = lesson.instructor_name ?? 'Instrutor'
  const dateStr = formatLessonDateTime(lesson.scheduled_at)

  return (
    <Card className="border-brand-200 shadow-soft bg-gradient-to-br from-brand-50 to-white">
      <CardContent className="p-5">
        <p className="text-xs font-semibold text-brand-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Calendar className="h-3 w-3" />
          Próxima aula
        </p>
        <p className="text-sm font-bold text-gray-900 truncate">{instructorName}</p>
        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
          <Clock className="h-3 w-3 shrink-0 text-brand-400" />
          {dateStr}
        </p>
        {lesson.pickup_address && (
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 shrink-0 text-teal-500" />
            {lesson.pickup_address}
          </p>
        )}
        <Link
          to={`/aluno/aulas/${lesson.id}`}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors"
          data-testid="next-lesson-details-btn"
        >
          Ver detalhes <ChevronRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  )
}


/** Widget: Progresso XP */
function MonthSummaryWidget() {
  const { data: summary, isLoading } = useStudentMonthSummary()

  if (isLoading) {
    return (
      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-5 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-12" />
          <Skeleton className="h-3 w-36" />
        </CardContent>
      </Card>
    )
  }

  const { completedCount, completionRate, averageRating } = summary ?? {
    completedCount: 0, completionRate: 0, averageRating: null,
  }

  return (
    <Card className="border-border/60 shadow-soft">
      <CardContent className="p-5">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Trophy className="h-3 w-3" />
          Resumo do mês
        </p>
        <p className="text-3xl font-extrabold text-gray-900">{completedCount}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          aula{completedCount !== 1 ? 's' : ''} concluída{completedCount !== 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3 text-teal-500" />
            {completionRate}% taxa de conclusão
          </span>
          {averageRating !== null && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
              {averageRating.toFixed(1)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// Skills derived from total hours (simulated progression)
const SKILL_AREAS = [
  { key: 'urban',     label: 'Direção urbana',     hoursNeeded: 5  },
  { key: 'parking',   label: 'Estacionamento',     hoursNeeded: 8  },
  { key: 'highway',   label: 'Rodovias',            hoursNeeded: 15 },
  { key: 'defensive', label: 'Direção defensiva',  hoursNeeded: 20 },
  { key: 'manual',    label: 'Câmbio manual',      hoursNeeded: 12 },
]

function ProgressSection({ totalCompleted, totalHours }: { totalCompleted: number; totalHours: number }) {
  const progressPercent = Math.min(100, Math.round((totalHours / CNH_GOAL_HOURS) * 100))
  const earnedAchievements = ACHIEVEMENTS.filter((a) => totalCompleted >= a.requiredLessons)
  const nextAchievement = ACHIEVEMENTS.find((a) => totalCompleted < a.requiredLessons)

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl border border-border/60 bg-white shadow-soft overflow-hidden"
    >
      <div className="px-5 pt-5 pb-4 border-b border-border/50">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Target className="h-4 w-4 text-brand-600" />
            Meu progresso
          </h2>
          <span className="text-xs text-muted-foreground">{totalHours}h de {CNH_GOAL_HOURS}h para a CNH</span>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-muted-foreground">{progressPercent}% concluído</span>
            {progressPercent < 100 && nextAchievement && (
              <span className="text-muted-foreground">
                Próxima conquista: {nextAchievement.requiredLessons - totalCompleted} aula{nextAchievement.requiredLessons - totalCompleted !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-600"
            />
          </div>
        </div>
      </div>

      {/* Achievements */}
      <div className="px-5 py-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Conquistas</p>
        <div className="flex flex-wrap gap-2">
          {ACHIEVEMENTS.map((achievement) => {
            const earned = totalCompleted >= achievement.requiredLessons
            return (
              <div
                key={achievement.id}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
                  earned ? achievement.color : 'bg-gray-100 text-gray-400 grayscale',
                )}
                title={earned ? `Conquistado! (${achievement.requiredLessons} aulas)` : `${achievement.requiredLessons - totalCompleted} aulas para conquistar`}
              >
                {achievement.icon}
                {achievement.label}
                {earned && <CheckCircle className="h-3 w-3" />}
              </div>
            )
          })}
        </div>
        {earnedAchievements.length === 0 && (
          <p className="text-xs text-muted-foreground mt-2">Complete sua primeira aula para desbloquear conquistas! 🎯</p>
        )}
      </div>

      {/* Skills breakdown */}
      <div className="px-5 pb-5 border-t border-border/50 pt-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Habilidades estimadas</p>
        <div className="space-y-2.5">
          {SKILL_AREAS.map((skill) => {
            const pct = Math.min(100, Math.round((totalHours / skill.hoursNeeded) * 100))
            const levelLabel = pct >= 100 ? 'Avançado' : pct >= 50 ? 'Intermediário' : 'Iniciante'
            const barColor = pct >= 100 ? 'from-green-500 to-emerald-600' : pct >= 50 ? 'from-brand-500 to-teal-500' : 'from-gray-300 to-gray-400'
            return (
              <div key={skill.key}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-700">{skill.label}</span>
                  <span className={`font-medium ${pct >= 100 ? 'text-green-600' : pct >= 50 ? 'text-brand-600' : 'text-gray-400'}`}>
                    {levelLabel}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className={`h-full rounded-full bg-gradient-to-r ${barColor}`}
                  />
                </div>
              </div>
            )
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Estimativa baseada no total de horas de aula. Converse com seu instrutor para uma avaliação precisa.
        </p>
      </div>
    </motion.section>
  )
}

// ─── Booking Calendar ─────────────────────────────────────────────────────────

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const STATUS_DOT: Record<string, string> = {
  confirmed:        'bg-brand-500',
  in_progress:      'bg-teal-500',
  completed:        'bg-green-500',
  pending_payment:  'bg-amber-400',
  cancelled_by_student:   'bg-red-400',
  cancelled_by_instructor: 'bg-red-400',
  no_show:          'bg-gray-400',
}

interface BookingCalendarProps {
  bookings: BookingWithDetails[]
}

function BookingCalendar({ bookings }: BookingCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState<Date | null>(null)

  const monthStart = startOfMonth(currentMonth)
  const monthEnd   = endOfMonth(currentMonth)

  // Build a 6-row grid starting from the Sunday of the first week
  const gridStart = new Date(monthStart)
  gridStart.setDate(monthStart.getDate() - monthStart.getDay())
  const gridEnd = new Date(gridStart)
  gridEnd.setDate(gridStart.getDate() + 41) // 42 cells

  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })

  const bookingsForDay = (day: Date) =>
    bookings.filter((b) => isSameDay(parseISO(b.scheduled_at), day))

  const selectedBookings = selectedDay ? bookingsForDay(selectedDay) : []

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl border border-border/60 bg-white shadow-soft overflow-hidden"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-border/50 flex items-center justify-between">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-brand-600" />
          Calendário de aulas
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
            className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-gray-600" />
          </button>
          <span className="text-sm font-semibold text-gray-700 w-32 text-center">
            {format(currentMonth, 'MMMM yyyy', { locale: ptBR })
              .replace(/^\w/, (c) => c.toUpperCase())}
          </span>
          <button
            onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
            className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="p-4">
        {/* Week day headers */}
        <div className="grid grid-cols-7 mb-1">
          {WEEK_DAYS.map((d) => (
            <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-xl overflow-hidden">
          {days.map((day) => {
            const dayBookings = bookingsForDay(day)
            const inMonth = isSameMonth(day, currentMonth)
            const today   = isToday(day)
            const selected = selectedDay ? isSameDay(day, selectedDay) : false

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => {
                  if (dayBookings.length > 0) {
                    setSelectedDay(selected ? null : day)
                  }
                }}
                className={cn(
                  'bg-white relative flex flex-col items-center pt-1.5 pb-1 min-h-[44px] transition-colors',
                  inMonth ? 'text-gray-800' : 'text-gray-300',
                  dayBookings.length > 0 && 'cursor-pointer hover:bg-brand-50',
                  selected && 'bg-brand-50',
                  !dayBookings.length && 'cursor-default',
                )}
              >
                <span
                  className={cn(
                    'h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium',
                    today && 'bg-brand-600 text-white font-bold',
                    selected && !today && 'bg-brand-100 text-brand-700',
                  )}
                >
                  {format(day, 'd')}
                </span>
                {dayBookings.length > 0 && (
                  <div className="flex gap-0.5 mt-0.5">
                    {dayBookings.slice(0, 3).map((b, i) => (
                      <span
                        key={i}
                        className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[b.status] ?? 'bg-gray-400')}
                      />
                    ))}
                  </div>
                )}
              </button>
            )
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-3 px-1">
          {[
            { color: 'bg-brand-500',  label: 'Confirmada' },
            { color: 'bg-green-500',  label: 'Concluída' },
            { color: 'bg-amber-400',  label: 'Aguardando pagamento' },
            { color: 'bg-red-400',    label: 'Cancelada' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className={cn('h-2 w-2 rounded-full', item.color)} />
              {item.label}
            </div>
          ))}
        </div>

        {/* Selected day bookings */}
        {selectedDay && selectedBookings.length > 0 && (
          <div className="mt-4 space-y-2 border-t pt-4">
            <p className="text-xs font-semibold text-gray-600 mb-2">
              {format(selectedDay, "d 'de' MMMM", { locale: ptBR })}
            </p>
            {selectedBookings.map((b) => {
              const instructorName = (b as any).instructor?.profile?.full_name ?? 'Instrutor'
              const time = format(parseISO(b.scheduled_at), 'HH:mm')
              return (
                <Link
                  key={b.id}
                  to={`/aluno/aulas/${b.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-brand-50 transition-colors"
                >
                  <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', STATUS_DOT[b.status] ?? 'bg-gray-400')} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{instructorName}</p>
                    <p className="text-xs text-muted-foreground">{time} · {getBookingStatusLabel(b.status)}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </motion.section>
  )
}

// ─── Packages section ─────────────────────────────────────────────────────────

// ─── Referral Section ────────────────────────────────────────────────────────

function ReferralSection({ userId }: { userId: string }) {
  const [copied, setCopied] = useState(false)
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [referralCredits, setReferralCredits] = useState<number>(0)

  // Fetch referral_code and referral_credits from profiles
  useEffect(() => {
    supabase
      .from('profiles')
      .select('referral_code, referral_credits')
      .eq('id', userId)
      .single()
      .then(({ data }) => {
        if (data) {
          setReferralCode(data.referral_code ?? null)
          setReferralCredits(Number(data.referral_credits ?? 0))
        }
      })
  }, [userId])

  const displayCode = referralCode ?? userId.slice(0, 8).toUpperCase()

  function copyCode() {
    navigator.clipboard.writeText(displayCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function shareLink() {
    const url = `${window.location.origin}/cadastro?ref=${displayCode}`
    if (navigator.share) {
      navigator.share({ title: 'MeuDriver', text: 'Aprenda a dirigir com os melhores instrutores!', url })
    } else {
      navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl overflow-hidden border border-border/60 bg-gradient-to-br from-orange-50 to-amber-50 shadow-soft"
    >
      <div className="px-5 pt-5 pb-4 border-b border-orange-200/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-orange-500" />
          <h2 className="text-base font-bold text-gray-900">Indique e ganhe</h2>
        </div>
        {referralCredits > 0 && (
          <span className="rounded-full bg-orange-100 px-3 py-0.5 text-sm font-semibold text-orange-700">
            Saldo: {formatCurrency(referralCredits)}
          </span>
        )}
      </div>

      <div className="px-5 py-5 space-y-4">
        <p className="text-sm text-gray-600 leading-relaxed">
          Convide amigos para o MeuDriver e <strong>ganhe R$ 30</strong> quando eles completarem a primeira aula paga. Eles ganham <strong>R$ 15</strong> de crédito também!
        </p>

        {/* Code display */}
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-white rounded-xl border border-orange-200 px-4 py-2.5 font-mono text-lg font-bold text-orange-600 tracking-widest text-center select-all">
            {displayCode}
          </div>
          <button
            onClick={copyCode}
            className="h-11 px-3 rounded-xl border border-orange-200 bg-white text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-colors flex items-center gap-1.5 text-sm font-medium shrink-0"
          >
            <Copy className="h-4 w-4" />
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
          <button
            onClick={shareLink}
            className="h-11 px-3 rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-colors flex items-center gap-1.5 text-sm font-semibold shrink-0"
          >
            <Share2 className="h-4 w-4" />
            Compartilhar
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-center text-xs text-gray-500">
          <div className="bg-white rounded-xl p-3 border border-orange-100">
            <p className="text-xl font-extrabold text-orange-500">R$ 30</p>
            <p>crédito para você</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-orange-100">
            <p className="text-xl font-extrabold text-brand-600">R$ 15</p>
            <p>crédito para o amigo</p>
          </div>
        </div>
      </div>
    </motion.section>
  )
}

// ─── Certificate Section ─────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function CertificateSection({ studentName }: { studentName: string }) {
  function printCertificate() {
    const win = window.open('', '_blank', 'width=900,height=650')
    if (!win) return
    const date = new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
    win.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Certificado — MeuDriver</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Open+Sans:wght@400;600&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Open Sans', Arial, sans-serif; background: #f8fafc; display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 40px; }
          .cert { background: white; border: 8px double #0054b3; max-width: 780px; width: 100%; padding: 56px 64px; text-align: center; position: relative; }
          .cert::before { content: ''; position: absolute; inset: 12px; border: 1px solid #e2e8f0; pointer-events: none; }
          .logo { font-size: 22px; font-weight: 900; color: #0054b3; letter-spacing: -0.5px; margin-bottom: 4px; }
          .subtitle { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 40px; }
          h1 { font-family: 'Playfair Display', Georgia, serif; font-size: 36px; color: #1e3a5f; margin-bottom: 24px; }
          .name { font-family: 'Playfair Display', Georgia, serif; font-size: 42px; color: #0054b3; margin: 16px 0; border-bottom: 2px solid #e2e8f0; padding-bottom: 16px; }
          .body { font-size: 15px; color: #475569; line-height: 1.8; margin: 24px 0; }
          .hours { font-size: 52px; font-weight: 900; color: #0d9488; line-height: 1; margin: 8px 0; }
          .hours-label { font-size: 13px; color: #64748b; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 32px; }
          .date { font-size: 13px; color: #94a3b8; margin-top: 40px; }
          .seal { font-size: 64px; margin: 16px 0; }
          @media print { body { padding: 0; background: white; } .cert { border-width: 6px; } }
        </style>
      </head>
      <body>
        <div class="cert">
          <p class="logo">MeuDriver</p>
          <p class="subtitle">Marketplace de Instrutores de Direção</p>
          <h1>Certificado de Conclusão</h1>
          <p class="body">Certificamos que</p>
          <p class="name">${escapeHtml(studentName)}</p>
          <p class="body">concluiu com êxito o programa de aulas práticas de direção,<br/>completando a carga horária mínima recomendada de</p>
          <p class="hours">20h</p>
          <p class="hours-label">horas de aulas práticas</p>
          <div class="seal">🏆</div>
          <p class="body">Este certificado reconhece a dedicação e o esforço do aluno<br/>na jornada rumo à habilitação.</p>
          <p class="date">Emitido em ${date} · MeuDriver — contato@meudriver.com.br</p>
        </div>
        <script>window.onload = () => window.print()</script>
      </body>
      </html>
    `)
    win.document.close()
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-500 to-teal-600 p-5 text-white shadow-soft"
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <Trophy className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-extrabold leading-tight">Parabéns! Meta atingida! 🎉</h2>
          <p className="text-sm text-white/80 mt-1 leading-relaxed">
            Você completou {CNH_GOAL_HOURS}h de aulas práticas. Seu certificado está pronto!
          </p>
          <Button
            size="sm"
            className="mt-3 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold gap-1.5"
            onClick={printCertificate}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Imprimir certificado
          </Button>
        </div>
      </div>
    </motion.section>
  )
}

// ─── History Section ──────────────────────────────────────────────────────────

type HistoryFilter = 'all' | 'upcoming' | 'completed' | 'cancelled'

function HistorySection({
  bookings,
  reviewedIds,
}: {
  bookings: BookingWithDetails[]
  reviewedIds: Set<string> | undefined
}) {
  const [filter, setFilter] = useState<HistoryFilter>('all')
  const [showAll, setShowAll] = useState(false)
  const PAGE = 5

  const filters: { key: HistoryFilter; label: string }[] = [
    { key: 'all', label: 'Todas' },
    { key: 'upcoming', label: 'Agendadas' },
    { key: 'completed', label: 'Concluídas' },
    { key: 'cancelled', label: 'Canceladas' },
  ]

  const filtered = bookings.filter((b) => {
    if (filter === 'all') return true
    if (filter === 'upcoming') return ['pending_payment', 'confirmed', 'in_progress'].includes(b.status)
    if (filter === 'completed') return b.status === 'completed'
    return b.status.startsWith('cancelled') || b.status === 'no_show'
  })

  const sorted = [...filtered].sort(
    (a, b) => parseISO(b.scheduled_at).getTime() - parseISO(a.scheduled_at).getTime(),
  )

  const visible = showAll ? sorted : sorted.slice(0, PAGE)

  return (
    <section>
      <SectionHeader
        icon={<History className="h-4 w-4 text-muted-foreground" />}
        title="Histórico de aulas"
      />

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {filters.map(({ key, label }) => {
          const count = bookings.filter((b) => {
            if (key === 'all') return true
            if (key === 'upcoming') return ['pending_payment', 'confirmed', 'in_progress'].includes(b.status)
            if (key === 'completed') return b.status === 'completed'
            return b.status.startsWith('cancelled') || b.status === 'no_show'
          }).length
          return (
            <button
              key={key}
              onClick={() => { setFilter(key); setShowAll(false) }}
              className={cn(
                'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                filter === key
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              )}
            >
              {label}
              {count > 0 && <span className="ml-1 opacity-75">({count})</span>}
            </button>
          )
        })}
      </div>

      {sorted.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-7 w-7 text-slate-400" />}
          title="Nenhuma aula encontrada"
          description={filter === 'all' ? 'Você ainda não tem aulas registradas.' : `Nenhuma aula ${filter === 'completed' ? 'concluída' : filter === 'upcoming' ? 'agendada' : 'cancelada'}.`}
        />
      ) : (
        <div className="space-y-3">
          {visible.map((booking) => {
            const instructor = booking.instructor
            const instructorName = instructor?.profile?.full_name ?? 'Instrutor'
            const avatarUrl = instructor?.profile?.avatar_url ?? undefined
            const statusColor = getBookingStatusColor(booking.status)
            const statusLabel = getBookingStatusLabel(booking.status)
            const hasReview = reviewedIds?.has(booking.id) ?? false

            return (
              <div
                key={booking.id}
                className="flex items-center gap-4 p-4 rounded-xl border border-border/60 bg-white hover:shadow-soft transition-shadow duration-200"
              >
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={avatarUrl} />
                  <AvatarFallback className="bg-brand-100 text-brand-700 font-semibold text-sm">
                    {getInitials(instructorName)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{instructorName}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatLessonDateTime(booking.scheduled_at)}
                  </p>
                  {booking.total_price != null && (
                    <p className="text-xs text-muted-foreground">{formatCurrency(booking.total_price)}</p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                    {statusLabel}
                  </span>
                  {booking.status === 'completed' && !hasReview ? (
                    <Link to={`/aluno/aulas/${booking.id}`}>
                      <Button size="sm" variant="outline" className="text-xs h-7 hover:border-brand-300 hover:text-brand-700">
                        Avaliar
                      </Button>
                    </Link>
                  ) : (
                    <Link
                      to={`/aluno/aulas/${booking.id}`}
                      className="text-xs text-brand-600 font-medium hover:text-brand-700 flex items-center gap-0.5 transition-colors"
                    >
                      Detalhes <ChevronRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
            )
          })}

          {sorted.length > PAGE && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="w-full py-2 text-sm text-muted-foreground hover:text-gray-700 transition-colors"
            >
              {showAll ? 'Mostrar menos' : `Ver mais ${sorted.length - PAGE} aulas`}
            </button>
          )}
        </div>
      )}
    </section>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function StudentDashboard() {
  useSEO({ title: 'Meu Dashboard', noIndex: true })

  const { profile, user } = useAuth()

  const { data: bookings, isLoading } = useStudentBookings()
  const { data: reviewedIds } = useStudentReviewedBookingIds()
  const { data: favorites, isLoading: favsLoading } = useFavorites()
  const toggleFav = useToggleFavorite()

  const allBookings = (bookings ?? []) as BookingWithDetails[]
  const favList = favorites ?? []

  const upcomingBookings = allBookings
    .filter((b) =>
      ['pending_payment', 'confirmed', 'in_progress'].includes(b.status) &&
      isFuture(parseISO(b.scheduled_at)),
    )
    .sort((a, b) => parseISO(a.scheduled_at).getTime() - parseISO(b.scheduled_at).getTime())
    .slice(0, 3)

  const allCompleted = allBookings.filter((b) => b.status === 'completed' && isPast(parseISO(b.scheduled_at)))

  const totalCompletedCount = allCompleted.length
  const totalHours = Math.round(
    allCompleted.reduce((sum, b) => sum + ((b as any).duration_minutes ?? 60), 0) / 60,
  )

  // Find next confirmed booking within 24 hours (lesson reminder)
  const now = new Date()
  const nextSoonBooking = allBookings
    .filter((b) => b.status === 'confirmed' && isFuture(parseISO(b.scheduled_at)))
    .sort((a, b) => parseISO(a.scheduled_at).getTime() - parseISO(b.scheduled_at).getTime())
    .find((b) => differenceInHours(parseISO(b.scheduled_at), now) <= 24) ?? null

  // Redirect new students without city to onboarding
  if (profile && !profile.city) {
    return <Navigate to="/aluno/onboarding" replace />
  }

  const firstName = profile?.full_name?.split(' ')[0] ?? 'Aluno'

  return (
    <div className="space-y-6 max-w-4xl mx-auto">

      {/* ── Onboarding tour ───────────────────────────────────────────────── */}
      <OnboardingTour />

      {/* ── Welcome header ────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col gap-1"
      >
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
          Olá, {firstName}! 👋
        </h1>
        <p className="text-muted-foreground text-sm">
          Sua próxima aula e o atalho para agendar ficam aqui em cima.
        </p>
      </motion.div>

      {/* ── Welcome banner (novos usuários) ──────────────────────────────── */}
      <WelcomeBanner name={profile?.full_name} />

      {/* ── Lesson reminder (24h) ─────────────────────────────────────────── */}
      {nextSoonBooking && (() => {
        const instructorName = (nextSoonBooking as any).instructor?.profile?.full_name ?? 'seu instrutor'
        const hoursLeft = differenceInHours(parseISO(nextSoonBooking.scheduled_at), now)
        const label = hoursLeft === 0 ? 'em menos de 1h' : `em ${hoursLeft}h`
        return (
          <Link to={`/aluno/aulas/${nextSoonBooking.id}`}>
            <div className="flex items-center gap-3 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 hover:bg-orange-100 transition-colors cursor-pointer">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-400/20">
                <Bell className="h-4 w-4 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-orange-800">
                  Sua aula começa {label}!
                </p>
                <p className="text-xs text-orange-600 mt-0.5 truncate">
                  {formatDateTime(nextSoonBooking.scheduled_at)} com {instructorName}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-orange-400 shrink-0" />
            </div>
          </Link>
        )
      })()}

      {/* ── Próxima aula + agendar ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="dashboard-widgets-grid">
        <NextLessonWidget />
        <div className="rounded-2xl overflow-hidden shadow-soft border border-border/60 bg-gradient-to-r from-brand-600 to-teal-600 p-5">
          <p className="text-sm font-semibold text-white/90 mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-orange-300" />
            Agendar uma aula
          </p>
          <p className="text-sm text-white/80 mb-4">
            {profile?.city ? `Instrutores em ${profile.city} e região.` : 'Encontre um instrutor perto de você.'}
          </p>
          <Button asChild variant="orange" className="h-10 px-5 font-semibold shadow-orange/30">
            <Link to="/aluno/instrutores">
              <Search className="h-4 w-4 mr-1.5" />
              Buscar instrutores
            </Link>
          </Button>
        </div>
      </div>

      {/* ── Active package widget ─────────────────────────────────────────── */}
      <ActivePackageWidget />

      {/* ── Stats row ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              icon={<Calendar className="h-5 w-5 text-white" />}
              value={upcomingBookings.length}
              label="Próximas aulas"
              gradient="bg-gradient-to-br from-brand-500 to-brand-700 shadow-brand/30"
              delay={0}
            />
            <StatCard
              icon={<CheckCircle className="h-5 w-5 text-white" />}
              value={totalCompletedCount}
              label="Aulas concluídas"
              gradient="bg-gradient-to-br from-teal-500 to-teal-700"
              delay={1}
            />
            <StatCard
              icon={<Heart className="h-5 w-5 text-white fill-current" />}
              value={favList.length}
              label="Favoritos"
              gradient="bg-gradient-to-br from-rose-400 to-rose-600"
              delay={2}
            />
          </>
        )}
      </div>

      {/* ── Certificate ───────────────────────────────────────────────────── */}
      {!isLoading && totalHours >= CNH_GOAL_HOURS && (
        <CertificateSection studentName={profile?.full_name ?? 'Aluno'} />
      )}

      {/* ── O resto em abas (antes era tudo empilhado: ~18.000 px no celular) ── */}
      <Tabs defaultValue="aulas" className="space-y-4">
        <TabsList className="w-full grid grid-cols-4 h-auto">
          <TabsTrigger value="aulas" className="text-xs sm:text-sm">Aulas</TabsTrigger>
          <TabsTrigger value="progresso" className="text-xs sm:text-sm">Progresso</TabsTrigger>
          <TabsTrigger value="beneficios" className="text-xs sm:text-sm">Benefícios</TabsTrigger>
          <TabsTrigger value="favoritos" className="text-xs sm:text-sm">Favoritos</TabsTrigger>
        </TabsList>

        {/* Aulas: próximas, histórico, calendário */}
        <TabsContent value="aulas" className="space-y-6">
          <section>
            <SectionHeader
              icon={<Calendar className="h-4 w-4 text-brand-600" />}
              title="Próximas aulas"
              action={
                upcomingBookings.length > 0 ? (
                  <Link to="/aluno/instrutores" className="text-sm text-brand-600 font-semibold hover:text-brand-700 transition-colors">
                    Agendar nova
                  </Link>
                ) : undefined
              }
            />
            {isLoading ? (
              <div className="space-y-3">
                <BookingCardSkeleton />
                <BookingCardSkeleton />
              </div>
            ) : upcomingBookings.length === 0 ? (
              <EmptyState
                icon={<Car className="h-7 w-7 text-slate-400" />}
                title="Nenhuma aula agendada"
                description="Encontre um instrutor e agende sua próxima aula prática."
                action={
                  <Button asChild size="sm">
                    <Link to="/aluno/instrutores">
                      <Search className="h-3.5 w-3.5 mr-1.5" />
                      Encontrar instrutor
                    </Link>
                  </Button>
                }
              />
            ) : (
              <div className="space-y-3">
                {upcomingBookings.map((booking) => (
                  <UpcomingCard key={booking.id} booking={booking} />
                ))}
              </div>
            )}
          </section>

          {!isLoading && allBookings.length > 0 && (
            <HistorySection bookings={allBookings} reviewedIds={reviewedIds} />
          )}

          {!isLoading && allBookings.length > 0 && (
            <BookingCalendar bookings={allBookings} />
          )}
        </TabsContent>

        {/* Progresso: horas até a CNH, habilidades e a trilha */}
        <TabsContent value="progresso" className="space-y-6">
          {!isLoading && (
            <ProgressSection totalCompleted={totalCompletedCount} totalHours={totalHours} />
          )}
          <MonthSummaryWidget />
          <section className="rounded-2xl border border-border/60 bg-white shadow-soft overflow-hidden">
            <div className="px-5 pt-5 pb-4 border-b border-border/50">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-brand-600" />
                Trilha de aprendizado
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                As etapas da sua jornada até a CNH.
              </p>
            </div>
            <div className="px-5 py-5">
              <LearningPathTimeline />
            </div>
          </section>
        </TabsContent>

        {/* Benefícios: pontos que viram desconto e indicação */}
        <TabsContent value="beneficios" className="space-y-6">
          <LoyaltyPointsCard />
          {user && <ReferralSection userId={user.id} />}
        </TabsContent>

        {/* Favoritos */}
        <TabsContent value="favoritos">
          <section>
            <SectionHeader
              icon={<Heart className="h-4 w-4 text-rose-500 fill-rose-500" />}
              title="Instrutores favoritos"
              action={
                favList.length > 0 ? (
                  <Link to="/aluno/instrutores" className="text-sm text-brand-600 font-semibold hover:text-brand-700 transition-colors">
                    Explorar mais
                  </Link>
                ) : undefined
              }
            />
            {favsLoading ? (
              <div className="space-y-3">
                <BookingCardSkeleton />
                <BookingCardSkeleton />
              </div>
            ) : favList.length === 0 ? (
              <EmptyState
                icon={<Heart className="h-7 w-7 text-slate-400" />}
                title="Nenhum favorito ainda"
                description="Ao favoritar um instrutor, ele aparece aqui para acesso rápido."
                action={
                  <Button asChild variant="outline" size="sm">
                    <Link to="/aluno/instrutores">Explorar instrutores</Link>
                  </Button>
                }
              />
            ) : (
              <div className="space-y-3">
                {favList.map((instructor) => (
                  <FavCard
                    key={instructor.id}
                    instructor={instructor}
                    onUnfav={(id) => toggleFav.mutate({ instructorId: id, isFav: true })}
                  />
                ))}
              </div>
            )}
          </section>
        </TabsContent>
      </Tabs>

    </div>
  )
}
